import {
  Button,
  Container,
  IconButton,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { User } from "../types";
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "../firebase";
import request from "../apis/request";
import { FirebaseError } from "firebase/app";
import { APIResponse, UserCreateDto } from "../types/apiTypes";
import { NavLink, useNavigate } from "react-router-dom";
import GoogleIcon from "@mui/icons-material/Google";
import Loading from "../components/common/Loading";
import { useDispatch } from "react-redux";
import { setUserInfo, setIsLoggedIn } from "../store/appSlice";
import { useTranslation } from "react-i18next";
import IconGoogle from "../components/sns/Google";

const LoginCard = styled("div")({
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "10px",
  boxShadow: "0 0 10px rgba(0,0,0,0.1)",
});

const Login = () => {
  const [inputUserId, setInputUserId] = useState("");
  const [inputUserPw, setInputUserPw] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { t: tCommon } = useTranslation("common");
  const { t: tError } = useTranslation("errors");

  const handleLogin = async () => {
    setIsProcessing(true);
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        inputUserId,
        inputUserPw,
      );

      const { uid, email, providerId, providerData } = credential.user;
      const idToken = await credential.user.getIdToken();

      // firestoreのuserコレクションに存在確認
      //   不在：追加
      //   存在：スール
      const registeredUser = await request<APIResponse<User, string>>({
        method: "GET",
        url: "userInfo",
        params: { authedId: uid, email },
        requireAuth: true,
        token: idToken,
      }).then((res) => res.data.data);

      dispatch(
        setUserInfo({
          id: registeredUser.id,
          authedId: uid,
          email,
          name: providerData[0].displayName || "",
          language: registeredUser?.language ?? "",
          currency: registeredUser?.currency ?? "",
          provider: providerId,
          token: idToken,
          options: credential.user,
        } as User),
      );

      dispatch(setIsLoggedIn(true));

      navigate("/setting", {
        state: {
          language: registeredUser.language,
          currency: registeredUser.currency,
        },
      });
    } catch (error) {
      console.error("error login: ", error);
      if (error instanceof FirebaseError) {
        alert(`${tError("error.login")}(${error.message})`);
      } else {
        alert(`${tError("error.login")}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLoginWithGoogle = async () => {
    console.log("login with google");
    setIsProcessing(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      console.log(`signin with google: ${result}`);

      const idToken = await result.user.getIdToken();
      const { uid, email, displayName, providerId } = result.user;

      const registeredUser = await request<APIResponse<User, UserCreateDto>>({
        method: "POST",
        url: "addUser",
        data: { authedId: uid, email, provider: provider.providerId },
      })
        .then((res) => {
          return res.data.data;
        })
        .catch(async (error) => {
          return await request<APIResponse<User, string>>({
            method: "GET",
            url: "userInfo",
            params: { email },
            requireAuth: true,
            token: idToken,
          })
            .then((res) => {
              return res.data.data;
            })
            .catch((error) => {
              throw new Error(
                "登録ユーザがあってるか確認のうえ管理者に問い合わせ",
              );
            });
        });

      if (registeredUser)
        dispatch(
          setUserInfo({
            id: registeredUser.id,
            authedId: uid,
            email,
            name: displayName || "",
            language: registeredUser?.language ?? "",
            currency: registeredUser?.currency ?? "",
            provider: providerId,
            token: idToken,
            options: result.user,
          } as User),
        );

      dispatch(setIsLoggedIn(true));

      navigate("/setting", {
        state: {
          language: registeredUser.language,
          currency: registeredUser.currency,
        },
      });
    } catch (error) {
      console.error("Emailでのログイン失敗:", error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <LoginCard>
        <Typography variant="h5" gutterBottom>
          {tCommon("auth.login")}
        </Typography>
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          label={tCommon("auth.email")}
          value={inputUserId}
          onChange={(e) => setInputUserId(e.target.value)}
        />
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          label={tCommon("auth.password")}
          type="password"
          value={inputUserPw}
          onChange={(e) => setInputUserPw(e.target.value)}
        />
        <Button
          sx={{ marginTop: "20px" }}
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleLogin}
        >
          {tCommon("auth.login")}
        </Button>
      </LoginCard>
      <LoginCard style={{ marginTop: "12px" }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<IconGoogle />}
          onClick={handleLoginWithGoogle}
        >
          {tCommon("auth.googleLogin")}
        </Button>
        <NavLink
          style={{
            display: "block",
            textAlign: "center",
            marginTop: "20px",
            textDecoration: "none",
          }}
          to={"/register"}
        >
          {tCommon("auth.register")}
        </NavLink>
      </LoginCard>
      {isProcessing && (
        <Loading message={tCommon("messages.processing")} blurBackdrop={true} />
      )}
    </Container>
  );
};

export default Login;
